







































































































































import { Component, Prop, Vue, Watch } from 'vue-property-decorator';
import { isEmail } from '@/jbi-shared/util/validate-email-domains.util';
import { FHIRClient } from '@/store/modules/fhir/types/fhir.types';
import { UpdateFHIRClientPayload } from '../types/admin-fhir-access-management.types';

const WILDCARD_DOMAIN_REGEX = /^\*\.[a-zA-Z0-9-]+\.[a-zA-Z]{2,}$/;
const SSH_ED25519_KEY_TYPE = 'ssh-ed25519';
const ED25519_RAW_KEY_LENGTH = 32;

function readLengthPrefixedField(
  bytes: Uint8Array,
  offset: number
): { value: Uint8Array; next: number } | undefined {
  if (offset + 4 > bytes.length) return undefined;
  const length =
    ((bytes[offset] << 24) |
      (bytes[offset + 1] << 16) |
      (bytes[offset + 2] << 8) |
      bytes[offset + 3]) >>>
    0;
  const start = offset + 4;
  const end = start + length;
  if (end > bytes.length) return undefined;
  return { value: bytes.slice(start, end), next: end };
}

/**
 * Mirrors the backend's ssh-ed25519 wire-format check (see
 * backend/.../ed25519-ssh-key.util.ts) so an obviously wrong paste — e.g.
 * an SSH key fingerprint instead of the key itself — is caught client-side
 * before it round-trips to the server.
 */
function isValidEd25519SshPublicKey(value: string): boolean {
  const [keyType, base64Blob] = value.trim().split(/\s+/);
  if (keyType !== SSH_ED25519_KEY_TYPE || !base64Blob) return false;

  let wireBlob: Uint8Array;
  try {
    const binary = atob(base64Blob);
    wireBlob = Uint8Array.from(binary, (char) => char.charCodeAt(0));
  } catch {
    return false;
  }

  const typeField = readLengthPrefixedField(wireBlob, 0);
  if (!typeField) return false;
  const typeStr = String.fromCharCode(...typeField.value);
  if (typeStr !== SSH_ED25519_KEY_TYPE) return false;

  const keyField = readLengthPrefixedField(wireBlob, typeField.next);
  if (!keyField) return false;
  return (
    keyField.value.length === ED25519_RAW_KEY_LENGTH &&
    keyField.next === wireBlob.length
  );
}

@Component
export default class EditFhirClientSidebar extends Vue {
  @Prop({ required: true })
  public client!: FHIRClient | undefined;

  @Prop({ default: false })
  public isSidebarOpen!: boolean;

  public form = {
    name: '',
    email: '',
    domain: '',
    publicKey: ''
  };

  public nameTouched = false;
  public emailTouched = false;
  public domainTouched = false;
  public publicKeyTouched = false;
  public isReplacingKey = false;

  get nameError(): string {
    if (!this.nameTouched) return '';
    return this.form.name.trim() ? '' : 'Client name is required.';
  }

  get emailError(): string {
    if (!this.emailTouched) return '';
    if (!this.form.email.trim()) return 'Email is required.';
    return isEmail(this.form.email) ? '' : 'Invalid email format.';
  }

  get domainError(): string {
    if (!this.domainTouched) return '';
    if (!this.form.domain.trim()) return 'Domain restriction is required.';
    return WILDCARD_DOMAIN_REGEX.test(this.form.domain.trim())
      ? ''
      : 'Domain must be in the format *.example.com.';
  }

  get publicKeyError(): string {
    if (!this.isReplacingKey || !this.publicKeyTouched) return '';
    if (!this.form.publicKey.trim()) return 'Public key is required.';
    return isValidEd25519SshPublicKey(this.form.publicKey) ? '' : 'Invalid key';
  }

  get isFormValid(): boolean {
    const detailsValid = !!(
      this.form.name.trim() &&
      this.form.email.trim() &&
      isEmail(this.form.email) &&
      this.form.domain.trim() &&
      WILDCARD_DOMAIN_REGEX.test(this.form.domain.trim())
    );
    if (!detailsValid) return false;
    if (!this.isReplacingKey) return true;
    return isValidEd25519SshPublicKey(this.form.publicKey);
  }

  @Watch('client')
  @Watch('isSidebarOpen')
  public onClientChange() {
    if (!this.client) return;
    this.form.name = this.client.name ?? '';
    this.form.email = this.client.email ?? '';
    this.form.domain = this.client.domain ? `${this.client.domain}` : '';
    this.form.publicKey = '';
    this.nameTouched = false;
    this.emailTouched = false;
    this.domainTouched = false;
    this.publicKeyTouched = false;
    this.isReplacingKey = false;
  }

  public onClose() {
    this.$emit('toggleSidebar', false);
  }

  public onReplaceKeyClick() {
    this.isReplacingKey = true;
    this.form.publicKey = '';
    this.publicKeyTouched = false;
  }

  public handleSave() {
    this.nameTouched = true;
    this.emailTouched = true;
    this.domainTouched = true;
    if (this.isReplacingKey) {
      this.publicKeyTouched = true;
    }
    if (!this.isFormValid) return;

    const payload: UpdateFHIRClientPayload = {
      name: this.form.name.trim(),
      email: this.form.email.trim(),
      domain: this.form.domain.trim()
    };
    if (this.isReplacingKey) {
      payload.publicKey = this.form.publicKey.trim();
    }

    this.$emit('save', payload);
    this.$emit('toggleSidebar', false);
  }
}
